// src/Components/NaturalQuery/renderers.tsx
import React from 'react';
import { FaUsers, FaBoxes, FaChartLine, FaCreditCard, FaDollarSign, FaShoppingCart } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { formatCurrency, formatDate, formatMonthName, getEmptyMessage } from './utils';

// Interfaces
interface Seller {
  vendor_id?: number;
  vendor_name?: string;
  total_sales?: number;
  total_value?: string | number;
}

interface Product {
  product_id?: number;
  product_name?: string;
  image?: string | null;
  total_qty?: string | number;
  total_value?: string | number;
}

interface MonthlyData {
  month?: number;
  year?: number;
  month_name?: string;
  month_year?: string;
  total_sales?: number;
  total_value?: number;
}

interface PaymentMethod {
  payment_method?: string;
  total_sales?: number;
  total_value?: number;
}

interface Store {
  store_id?: number;
  store_name?: string;
  total_store_value?: number;
  total_store_sales?: number;
  payment_methods?: PaymentMethod[];
}

interface ExpenseIncomeItem {
  description?: string;
  due_date?: string;
  amount?: number;
}

// Cores para gráficos
const COLORS = ['#667eea', '#764ba2', '#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1', '#fd7e14'];

// Componente para renderizar estado vazio
export const renderEmptyState = (dataType: string, period?: { start_date?: string; end_date?: string }) => (
  <div style={{ 
    marginTop: '15px', 
    padding: '20px', 
    backgroundColor: '#f8f9fa', 
    border: '1px solid #dee2e6', 
    borderRadius: '8px',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '24px', marginBottom: '10px', opacity: 0.6 }}>
      📊
    </div>
    <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '8px', fontWeight: '500' }}>
      Nenhum resultado encontrado
    </div>
    <div style={{ fontSize: '12px', color: '#6c757d', lineHeight: '1.4' }}>
      {getEmptyMessage(dataType)}
      {period && (
        <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.8 }}>
          Período consultado: {formatDate(period.start_date || '')} até {formatDate(period.end_date || '')}
        </div>
      )}
    </div>
  </div>
);

// Componente para renderizar vendedores
export const renderSellers = (sellers: Seller[]) => {
  // Preparar dados para o gráfico
  const chartData = sellers.map(seller => ({
    name: seller.vendor_name?.substring(0, 15) + (seller.vendor_name && seller.vendor_name.length > 15 ? '...' : ''),
    fullName: seller.vendor_name,
    value: parseFloat(seller.total_value?.toString() || '0'),
    sales: seller.total_sales || 0
  }));

  return (
    <div style={{ marginTop: '15px' }}>
      <h6 style={{ color: '#495057', marginBottom: '15px', fontSize: '14px' }}>
        <FaUsers style={{ marginRight: '8px', color: '#007bff' }} />
        Vendedores
      </h6>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '20px', marginBottom: '15px' }}>
        {/* Lista de vendedores */}
        <div style={{ display: 'grid', gap: '8px' }}>
          {sellers.map((seller, index) => (
            <div key={index} style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: '600', color: '#007bff', fontSize: '13px' }}>
                  {seller.vendor_name}
                </div>
                <div style={{ fontSize: '11px', color: '#6c757d' }}>
                  {seller.total_sales} vendas
                </div>
              </div>
              <div style={{
                backgroundColor: '#d4edda',
                color: '#28a745',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {formatCurrency(seller.total_value || 0)}
              </div>
            </div>
          ))}
        </div>

        {/* Gráfico de barras */}
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #dee2e6', borderRadius: '8px', padding: '15px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '10px', color: '#495057' }}>
            Vendas por Funcionário
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" fontSize={10} />
              <YAxis fontSize={10} tickFormatter={(value) => `R$ ${value.toLocaleString()}`} />
              <Tooltip 
                formatter={(value, name) => [`R$ ${parseFloat(value.toString()).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Vendas']}
                labelFormatter={(label) => {
                  const item = chartData.find(d => d.name === label);
                  return item ? item.fullName : label;
                }}
              />
              <Bar dataKey="value" fill="#667eea" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// Componente para renderizar produtos
export const renderProducts = (products: Product[]) => {
  // Preparar dados para o gráfico
  const chartData = products.slice(0, 6).map((product, index) => ({
    name: product.product_name?.substring(0, 12) + (product.product_name && product.product_name.length > 12 ? '...' : ''),
    fullName: product.product_name,
    value: parseFloat(product.total_value?.toString() || '0'),
    qty: parseFloat(product.total_qty?.toString() || '0'),
    color: COLORS[index % COLORS.length]
  }));

  return (
    <div style={{ marginTop: '15px' }}>
      <h6 style={{ color: '#495057', marginBottom: '15px', fontSize: '14px' }}>
        <FaBoxes style={{ marginRight: '8px', color: '#28a745' }} />
        Produtos
      </h6>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '20px', marginBottom: '15px' }}>
        {/* Lista de produtos */}
        <div style={{ display: 'grid', gap: '8px' }}>
          {products.map((product, index) => (
            <div key={index} style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {product.image && (
                  <img 
                    src={product.image} 
                    alt={product.product_name}
                    style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }}
                  />
                )}
                <div>
                  <div style={{ fontWeight: '600', color: '#495057', fontSize: '13px' }}>
                    {product.product_name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>
                    Qtd: {product.total_qty}
                  </div>
                </div>
              </div>
              <div style={{
                backgroundColor: '#d4edda',
                color: '#28a745',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {formatCurrency(product.total_value || 0)}
              </div>
            </div>
          ))}
        </div>

        {/* Gráfico de pizza */}
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #dee2e6', borderRadius: '8px', padding: '15px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '10px', color: '#495057' }}>
            Distribuição por Produto (Top 6)
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
                fontSize={10}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`R$ ${parseFloat(value.toString()).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Vendas']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// Componente para renderizar faturamento mensal
export const renderMonthlyData = (data: MonthlyData[]) => {
  // Preparar dados para o gráfico
  const chartData = data.map(item => ({
    name: formatMonthName(item.month || 0, item.year || 0).substring(0, 10),
    fullName: formatMonthName(item.month || 0, item.year || 0),
    value: item.total_value || 0,
    sales: item.total_sales || 0
  }));

  return (
    <div style={{ marginTop: '15px' }}>
      <h6 style={{ color: '#495057', marginBottom: '15px', fontSize: '14px' }}>
        <FaChartLine style={{ marginRight: '8px', color: '#dc3545' }} />
        Faturamento Mensal
      </h6>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 450px', gap: '20px', marginBottom: '15px' }}>
        {/* Lista de dados mensais */}
        <div style={{ display: 'grid', gap: '8px' }}>
          {data.map((item, index) => (
            <div key={index} style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: '600', color: '#495057', fontSize: '13px' }}>
                  {formatMonthName(item.month || 0, item.year || 0)}
                </div>
                <div style={{ fontSize: '11px', color: '#6c757d' }}>
                  {item.total_sales} vendas
                </div>
              </div>
              <div style={{
                backgroundColor: item.total_value && item.total_value > 0 ? '#d4edda' : '#f8d7da',
                color: item.total_value && item.total_value > 0 ? '#28a745' : '#721c24',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {formatCurrency(item.total_value || 0)}
              </div>
            </div>
          ))}
        </div>

        {/* Gráfico de linha */}
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #dee2e6', borderRadius: '8px', padding: '15px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '10px', color: '#495057' }}>
            Evolução do Faturamento
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" fontSize={10} />
              <YAxis fontSize={10} tickFormatter={(value) => `R$ ${value.toLocaleString()}`} />
              <Tooltip 
                formatter={(value) => [`R$ ${parseFloat(value.toString()).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Faturamento']}
                labelFormatter={(label) => {
                  const item = chartData.find(d => d.name === label);
                  return item ? item.fullName : label;
                }}
              />
              <Line type="monotone" dataKey="value" stroke="#667eea" strokeWidth={2} dot={{ fill: '#667eea' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// Componente para renderizar métodos de pagamento
export const renderPaymentMethods = (stores: Store[]) => {
  // Preparar dados para o gráfico (todos os métodos de pagamento de todas as lojas)
  const allPaymentMethods: Array<{name: string, value: number, sales: number}> = [];
  
  stores.forEach(store => {
    store.payment_methods?.forEach(method => {
      allPaymentMethods.push({
        name: method.payment_method?.substring(0, 20) + (method.payment_method && method.payment_method.length > 20 ? '...' : '') || '',
        value: method.total_value || 0,
        sales: method.total_sales || 0
      });
    });
  });

  return (
    <div style={{ marginTop: '15px' }}>
      <h6 style={{ color: '#495057', marginBottom: '15px', fontSize: '14px' }}>
        <FaCreditCard style={{ marginRight: '8px', color: '#6f42c1' }} />
        Faturamento por Forma de Pagamento
      </h6>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '20px' }}>
        {/* Lista de métodos de pagamento */}
        <div>
          {stores.map((store, storeIndex) => (
            <div key={storeIndex} style={{ marginBottom: '15px' }}>
              <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>
                <strong>{store.store_name}</strong> - Total: {formatCurrency(store.total_store_value || 0)}
              </div>
              <div style={{ display: 'grid', gap: '6px' }}>
                {store.payment_methods?.map((method, index) => (
                  <div key={index} style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    padding: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#495057' }}>
                        {method.payment_method}
                      </div>
                      <div style={{ fontSize: '10px', color: '#6c757d' }}>
                        {method.total_sales} transações
                      </div>
                    </div>
                    <div style={{
                      backgroundColor: '#d4edda',
                      color: '#28a745',
                      padding: '3px 6px',
                      borderRadius: '3px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {formatCurrency(method.total_value || 0)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Gráfico de barras horizontais */}
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #dee2e6', borderRadius: '8px', padding: '15px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '10px', color: '#495057' }}>
            Distribuição por Método
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={allPaymentMethods} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" fontSize={10} tickFormatter={(value) => `R$ ${value.toLocaleString()}`} />
              <YAxis dataKey="name" type="category" fontSize={9} width={80} />
              <Tooltip formatter={(value) => [`R$ ${parseFloat(value.toString()).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Valor']} />
              <Bar dataKey="value" fill="#6f42c1" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// Componente para renderizar despesas e receitas
export const renderExpensesIncome = (receitas?: ExpenseIncomeItem[], despesas?: ExpenseIncomeItem[]) => {
  // Preparar dados para o gráfico
  const chartData = [
    {
      name: 'Receitas',
      value: receitas?.reduce((total, item) => total + (item.amount || 0), 0) || 0,
      color: '#28a745'
    },
    {
      name: 'Despesas', 
      value: despesas?.reduce((total, item) => total + (item.amount || 0), 0) || 0,
      color: '#dc3545'
    }
  ];

  return (
    <div style={{ marginTop: '15px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px' }}>
        {/* Listas de receitas e despesas */}
        <div>
          {receitas && receitas.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <h6 style={{ color: '#495057', marginBottom: '10px', fontSize: '14px' }}>
                <FaDollarSign style={{ marginRight: '8px', color: '#28a745' }} />
                Receitas
              </h6>
              <div style={{ display: 'grid', gap: '6px' }}>
                {receitas.map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    borderRadius: '6px',
                    padding: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#155724', fontWeight: '500' }}>
                        {item.description}
                      </div>
                      <div style={{ fontSize: '10px', color: '#6c757d' }}>
                        {formatDate(item.due_date || '')}
                      </div>
                    </div>
                    <div style={{
                      color: '#28a745',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {formatCurrency(item.amount || 0)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {despesas && despesas.length > 0 && (
            <div>
              <h6 style={{ color: '#495057', marginBottom: '10px', fontSize: '14px' }}>
                <FaShoppingCart style={{ marginRight: '8px', color: '#dc3545' }} />
                Despesas
              </h6>
              <div style={{ display: 'grid', gap: '6px' }}>
                {despesas.map((item, index) => (
                  <div key={index} style={{
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '6px',
                    padding: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#721c24', fontWeight: '500' }}>
                        {item.description}
                      </div>
                      <div style={{ fontSize: '10px', color: '#6c757d' }}>
                        {formatDate(item.due_date || '')}
                      </div>
                    </div>
                    <div style={{
                      color: '#dc3545',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {formatCurrency(item.amount || 0)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Gráfico de pizza receitas vs despesas */}
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #dee2e6', borderRadius: '8px', padding: '15px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '10px', color: '#495057' }}>
            Receitas vs Despesas
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={chartData.filter(item => item.value > 0)}
                cx="50%"
                cy="50%"
                outerRadius={60}
                dataKey="value"
                label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
                fontSize={10}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`R$ ${parseFloat(value.toString()).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Valor']} />
            </PieChart>
          </ResponsiveContainer>
          
          {/* Resumo */}
          <div style={{ marginTop: '10px', fontSize: '11px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: '#28a745' }}>Total Receitas:</span>
              <strong style={{ color: '#28a745' }}>{formatCurrency(chartData[0].value)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: '#dc3545' }}>Total Despesas:</span>
              <strong style={{ color: '#dc3545' }}>{formatCurrency(chartData[1].value)}</strong>
            </div>
            <hr style={{ margin: '6px 0', borderColor: '#dee2e6' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Saldo:</span>
              <strong style={{ color: chartData[0].value - chartData[1].value >= 0 ? '#28a745' : '#dc3545' }}>
                {formatCurrency(chartData[0].value - chartData[1].value)}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};